import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { LightXSmall, TitleXSmall } from '@shared/components/core/text/Typography';
import { IAlertNotificationProps } from '@shared/models/components/IAlertNotificationProps';
import { AlertAccentWrapper, NotificationContent, NotificationSubtitleAction } from './CommonAlertNotificationStyles';

/**
 * Banner is a component that displays a banner with a title, subtitle, and icon.
 *
 * - `default`: allows unstyled button and text buttons to be used for constructing custom notification cards
 * - `accent` & `info`: uses the secondary colors and should be used for informational notifications
 * - `warning`: uses the warning color should be used for warning notifications
 * - `error`: uses the error color should be used for error notifications
 * - `success`: uses the success color should be used for success notifications
 *
 *  */

export default function Banner({
    color = 'default',
    title,
    subtitle,
    subtitleAction,
    icon,
    children,
    borderless = false,
}: IAlertNotificationProps) {
    const theme = useTheme();
    if (!title) return null;
    return (
        <AlertAccentWrapper $color={color} $borderless={borderless}>
            <FlexRow>
                <FlexWrap>
                    <IconRow>
                        {icon && <Icons size={18} name={icon} color={theme.banner[color].icon} />}
                        <TitleXSmall as="h2">{title}</TitleXSmall>
                    </IconRow>
                    {subtitle && (
                        <NotificationSubtitleAction $color={color}>
                            {subtitle && <StyledLightXSmall>{subtitle}</StyledLightXSmall>}
                            {subtitleAction}
                        </NotificationSubtitleAction>
                    )}
                </FlexWrap>
                <NotificationContent $color={color}>{children}</NotificationContent>
            </FlexRow>
        </AlertAccentWrapper>
    );
}

const FlexWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex-wrap: wrap;
`;

const FlexRow = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    gap: 0.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;
const IconRow = styled(FlexRow)`
    justify-content: flex-start;
`;
const StyledLightXSmall = styled(LightXSmall)`
    display: inline;
`;
